<template>
  <div class="data-display-wrapper">
    <!-- 加载状态 -->
    <Spin v-if="loading" fix>
      <div class="loading-text">
        <Icon type="ios-loading" size="30" class="spin-icon-load"></Icon>
        <div>{{ langMap.dashboard_loading[lang] }}</div>
      </div>
    </Spin>
    
    <!-- 空状态 -->
    <div v-if="!loading && !hasData" class="empty-state">
      <div class="empty-icon-wrapper">
        <div class="hologram-ring"></div>
        <div class="hologram-ring delay-1"></div>
        <div class="hologram-ring delay-2"></div>
        <div class="empty-icon">💾</div>
      </div>
      <div class="empty-text">{{ langMap.empty[lang] }}</div>
      <div class="empty-subtext">{{ emptyMessage }}</div>
    </div>
    
    <!-- 数据表格 -->
    <div v-if="!loading && hasData" class="data-content">
      <div class="table-wrapper">
        <Table
          :columns="columns"
          :data="tableData"
          stripe
          border
          size="small"
          :height="tableHeight"
        >
          <template slot-scope="{ row }" slot="dataTime">
            {{ formatTime(row.dataTime) }}
          </template>
          
          <!-- 挂载信息 -->
          <template slot-scope="{ row }" slot="mount">
            <div class="mount-compact">
              <div class="compact-row">
                <span class="row-label">{{ langMap.dashboard_mountPoint[lang] }}</span>
                <span class="row-value">{{ row.mountPoint }}</span>
              </div>
              <div class="compact-row">
                <span class="row-label">{{ langMap.dashboard_fileSystem[lang] }}</span>
                <span class="row-value">{{ row.fsType }}</span>
              </div>
            </div>
          </template>

          <!-- 容量信息 -->
          <template slot-scope="{ row }" slot="capacity">
            <div class="capacity-compact">
              <div class="compact-row">
                <span class="row-label">{{ langMap.dashboard_totalCapacity[lang] }}</span>
                <span class="row-value">{{ formatBytes(row.totalCapacity) }}</span>
              </div>
              <div class="compact-row">
                <span class="row-label">{{ langMap.dashboard_freeCapacity[lang] }}</span>
                <span class="row-value">{{ formatBytes(row.freeCapacity) }}</span>
              </div>
            </div>
          </template>

          <!-- 使用率 -->
          <template slot-scope="{ row }" slot="usage">
            <div class="usage-info">
              <span class="usage-value" :class="getUsageClass(row.usageRate)">
                {{ row.usageRate }}%
              </span>
              <div class="usage-bar">
                <div class="usage-fill" :style="{ width: row.usageRate + '%', backgroundColor: getUsageColor(row.usageRate) }"></div>
              </div>
            </div>
          </template>
        </Table>
      </div>
      
      <!-- 自定义分页器 -->
      <div class="pagination-wrapper">
        <div class="cyber-pagination">
          <!-- 总数信息 -->
          <div class="pagination-info">
            <span class="info-label">{{ langMap.dashboard_totalRecords[lang] }}</span>
            <span class="info-value">{{ pagination.total }}</span>
            <span class="info-divider">|</span>
            <span class="info-text">{{ pageRangeText }}</span>
          </div>
          
          <!-- 页码按钮组 -->
          <div class="pagination-controls">
            <!-- 首页 -->
            <button 
              class="page-btn page-first"
              :disabled="pagination.current === 1"
              @click="handlePageChange(1)"
            >
              <span class="btn-icon">《《</span>
            </button>
            
            <!-- 上一页 -->
            <button 
              class="page-btn page-prev"
              :disabled="pagination.current === 1"
              @click="handlePageChange(pagination.current - 1)"
            >
              <span class="btn-icon">‹</span>
            </button>
            
            <!-- 页码按钮 -->
            <div class="page-numbers">
              <button
                v-for="page in visiblePages"
                :key="page"
                :class="['page-number', { active: page === pagination.current, ellipsis: page === '...' }]"
                :disabled="page === '...'"
                @click="page !== '...' && handlePageChange(page)"
              >
                <span class="number-glow" v-if="page === pagination.current"></span>
                {{ page }}
              </button>
            </div>
            
            <!-- 下一页 -->
            <button 
              class="page-btn page-next"
              :disabled="pagination.current === totalPages"
              @click="handlePageChange(pagination.current + 1)"
            >
              <span class="btn-icon">›</span>
            </button>
            
            <!-- 尾页 -->
            <button 
              class="page-btn page-last"
              :disabled="pagination.current === totalPages"
              @click="handlePageChange(totalPages)"
            >
              <span class="btn-icon">》》</span>
            </button>
          </div>
          
          <!-- 每页条数选择 -->
          <div class="page-size-selector">
            <span class="selector-label">{{ langMap.dashboard_perPage[lang] }}</span>
            <div class="size-options">
              <button
                v-for="size in pageSizeOptions"
                :key="size"
                :class="['size-option', { active: pagination.size === size }]"
                @click="handlePageSizeChange(size)"
              >
                {{ size }}
              </button>
            </div>
          </div>
          
          <!-- 快速跳转 -->
          <div class="page-jumper">
            <span class="jumper-label">{{ langMap.dashboard_jumpTo[lang] }}</span>
            <input 
              type="number" 
              class="jumper-input"
              v-model.number="jumpPage"
              @keyup.enter="handleJumpPage"
              :placeholder="String(pagination.current)"
              min="1"
              :max="totalPages"
            />
            <button class="jumper-btn" @click="handleJumpPage">
              <span class="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import langMap from '@/utils/language'
import { mapState } from 'vuex';

export default {
  name: 'DiskHistoryTable',
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    hasData: {
      type: Boolean,
      default: false
    },
    emptyMessage: {
      type: String,
      default: ''
    },
    tableData: {
      type: Array,
      default: () => []
    },
    pagination: {
      type: Object,
      default: () => ({
        total: 0,
        current: 1,
        size: 20,
        pages: 0
      })
    }
  },
  data() {
    return {
      langMap,
      tableHeight: 500,
      pageSizeOptions: [10, 20, 30, 50],
      jumpPage: null,
      windowHeight: window.innerHeight,
      columns: [
        {
          title: langMap.dashboard_dataTime[this.$store.state.app.language],
          slot: 'dataTime',
          width: 150,
          align: 'center'
        },
        {
          title: langMap.dashboard_deviceName[this.$store.state.app.language],
          key: 'deviceName',
          width: 130,
          align: 'center'
        },
        {
          title: langMap.dashboard_mountInfo[this.$store.state.app.language],
          slot: 'mount',
          minWidth: 180,
          align: 'left'
        },
        {
          title: langMap.dashboard_capacityInfo[this.$store.state.app.language],
          slot: 'capacity',
          minWidth: 200,
          align: 'left'
        },
        {
          title: langMap.dashboard_usageRate[this.$store.state.app.language],
          slot: 'usage',
          width: 200,
          align: 'center'
        }
      ]
    }
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    }),
    tableHeight() {
      return Math.max(400, this.windowHeight - 502);
    },
    totalPages() {
      return Math.ceil(this.pagination.total / this.pagination.size) || 1;
    },
    pageRangeText() {
      const start = (this.pagination.current - 1) * this.pagination.size + 1;
      const end = Math.min(this.pagination.current * this.pagination.size, this.pagination.total);
      return `${start}-${end}`;
    },
    visiblePages() {
      const current = this.pagination.current;
      const total = this.totalPages;
      const pages = [];
      
      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) {
            pages.push(i);
          }
          pages.push('...');
          pages.push(total);
        } else if (current >= total - 3) {
          pages.push(1);
          pages.push('...');
          for (let i = total - 4; i <= total; i++) {
            pages.push(i);
          }
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = current - 1; i <= current + 1; i++) {
            pages.push(i);
          }
          pages.push('...');
          pages.push(total);
        }
      }
      
      return pages;
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.updateColumnsTitle();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  watch: {
    lang() {
      this.updateColumnsTitle();
    }
  },
  methods: {
    updateColumnsTitle() {
      this.columns[0].title = this.langMap.dashboard_dataTime[this.lang];
      this.columns[1].title = this.langMap.dashboard_deviceName[this.lang];
      this.columns[2].title = this.langMap.dashboard_mountInfo[this.lang];
      this.columns[3].title = this.langMap.dashboard_capacityInfo[this.lang];
      this.columns[4].title = this.langMap.dashboard_usageRate[this.lang];
    },
    formatTime(timestamp) {
      if (!timestamp) return '-';
      const date = new Date(timestamp);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
    formatBytes(bytes) {
      if (!bytes || bytes === 0) return '0 G';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + 'G';
    },
    getUsageClass(usage) {
      const u = parseFloat(usage);
      if (u >= 90) return 'high';
      if (u >= 70) return 'medium';
      return 'normal';
    },
    getUsageColor(usage) {
      const u = parseFloat(usage);
      if (u >= 90) return '#ff4d4f';
      if (u >= 70) return '#faad14';
      return '#52c41a';
    },
    handlePageChange(page) {
      this.$emit('page-change', page);
    },
    handlePageSizeChange(size) {
      this.$emit('size-change', size);
    },
    handleJumpPage() {
      if (!this.jumpPage || this.jumpPage < 1 || this.jumpPage > this.totalPages) {
        this.$Message.warning(this.langMap.dashboard_inputValidPageRange[this.lang].replace('{max}', this.totalPages));
        return;
      }
      this.handlePageChange(this.jumpPage);
      this.jumpPage = null;
    },
    handleResize() {
      this.windowHeight = window.innerHeight;
    }
  }
}
</script>

<style lang="less" scoped>
.data-display-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  .mount-compact,
  .capacity-compact {
    display: flex;
    flex-direction: column;
    gap: 4px;
    height: 100%;
    
    .compact-row {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 5px 8px;
      border-bottom: 1px solid rgba(0, 240, 255, 0.15);
      transition: all 0.2s ease;
      text-align: left;
      &:last-child {
        border-bottom: none;
      }
      
      &:hover {
        background: rgba(0, 240, 255, 0.06);
        padding-left: 12px;
      }
      
      .row-label {
        font-size: 11px;
        color: rgba(255, 255, 255, 0.55);
        font-weight: 600;
        letter-spacing: 0.5px;
        min-width: 60px;
        text-align: left;
      }
      
      .row-value {
        font-size: 12px;
        font-weight: 700;
        color: #fff;
        font-family: 'Courier New', monospace;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        flex: 1;
      }
    }
  }
  
  .mount-compact {
    .compact-row:nth-child(1) .row-value {
      color: #00f0ff;
      text-shadow: 0 0 6px rgba(0, 240, 255, 0.5);
    }
    
    .compact-row:nth-child(2) .row-value {
      color: #39FF14;
      text-shadow: 0 0 6px rgba(57, 255, 20, 0.5);
    }
  }
  
  .capacity-compact {
    .compact-row:nth-child(1) .row-value {
      color: #FFA500;
      text-shadow: 0 0 6px rgba(255, 165, 0, 0.5);
    }
    
    .compact-row:nth-child(2) .row-value {
      color: #52c41a;
      text-shadow: 0 0 6px rgba(82, 196, 26, 0.5);
    }
  }
  
  .usage-info {
    display: flex;
    flex-direction: column;
    gap: 6px;
    
    .usage-value {
      font-weight: 700;
      font-family: 'Courier New', monospace;
      font-size: 14px;
      
      &.high { color: #ff4d4f; }
      &.medium { color: #faad14; }
      &.normal { color: #52c41a; }
    }
    
    .usage-bar {
      width: 100%;
      height: 6px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 3px;
      overflow: hidden;
      
      .usage-fill {
        height: 100%;
        transition: width 0.3s ease;
        border-radius: 3px;
      }
    }
  }
}
</style>
